import React from 'react';
import { Box, Container, Typography, List, ListItem, ListItemContent, Link, Divider } from '@mui/joy';
import { animate, stagger } from 'motion';
import { useEffect, useRef } from 'react';

const Refund = () => {
    const containerRef = useRef(null);

    useEffect(() => {
        if (!containerRef.current) return;

        const elements = containerRef.current.querySelectorAll('.animate-in');
        
        animate(
            elements,
            { 
                opacity: [0, 1],
                y: [20, 0]
            },
            { 
                delay: stagger(0.1),
                duration: 0.6,
                easing: 'ease-out'
            }
        );
    }, []);

    return (
        <Box ref={containerRef}>
            <Container sx={{ py: 8 }}>
                <Box className="animate-in" sx={{ textAlign: 'center', mb: 8 }}>
                    <Typography
                        level="h1"
                        fontSize="xl4"
                        sx={{
                            mb: 3,
                            bgcolor: 'text.primary',
                            WebkitBackgroundClip: 'text',
                            WebkitTextFillColor: 'transparent',
                        }}
                    >
                        Fair Refund Policy
                    </Typography>
                    <Typography 
                        level="body1" 
                        fontSize="lg" 
                        sx={{ 
                            maxWidth: 800, 
                            mx: 'auto',
                            color: 'text.secondary',
                            lineHeight: 1.6
                        }}
                    >
                        At Hubitable, we believe in fair and transparent refund policies that comply with UK consumer laws, including the <strong>Consumer Rights Act 2015</strong> and <strong>Consumer Contracts Regulations 2013</strong>.
                    </Typography>
                </Box>

                <Box className="animate-in" sx={{ maxWidth: 800, mx: 'auto' }}>
                    <Typography level="h2" sx={{ mb: 3, color: 'text.primary' }}>
                        1. Statutory Refund Rights
                    </Typography>
                    <List size="lg" sx={{ mb: 4 }}>
                        <ListItem>
                            <ListItemContent>
                                <Typography level="body1">
                                    <strong>14-Day Cooling-Off Period</strong>: UK consumers have the right to cancel digital services within 14 days of purchase, as per the Consumer Contracts Regulations 2013.
                                </Typography>
                            </ListItemContent>
                        </ListItem>
                        <ListItem>
                            <ListItemContent>
                                <Typography level="body1">
                                    <strong>Faulty Services</strong>: If our services are not as described or unfit for purpose, you have rights under the Consumer Rights Act 2015.
                                </Typography>
                            </ListItemContent>
                        </ListItem>
                    </List>

                    <Divider sx={{ my: 4 }} />

                    <Typography level="h2" sx={{ mb: 3, color: 'text.primary' }}>
                        2. Discretionary Refunds
                    </Typography>
                    <List size="lg" sx={{ mb: 4 }}>
                        <ListItem>
                            <ListItemContent>
                                <Typography level="body1">
                                    <strong>Service Unavailability</strong>: If our services are unavailable for an extended period, we may offer partial refunds.
                                </Typography>
                            </ListItemContent>
                        </ListItem>
                        <ListItem>
                            <ListItemContent>
                                <Typography level="body1">
                                    <strong>Billing Errors</strong>: We'll refund any overcharges or billing mistakes promptly.
                                </Typography>
                            </ListItemContent>
                        </ListItem>
                        <ListItem>
                            <ListItemContent>
                                <Typography level="body1">
                                    <strong>Special Circumstances</strong>: We may consider refunds in exceptional cases, evaluated on a case-by-case basis.
                                </Typography>
                            </ListItemContent>
                        </ListItem>
                    </List>

                    <Divider sx={{ my: 4 }} />

                    <Typography level="h2" sx={{ mb: 3, color: 'text.primary' }}>
                        3. How to Request a Refund
                    </Typography>
                    <List size="lg" sx={{ mb: 4 }}>
                        <ListItem>
                            <ListItemContent>
                                <Typography level="body1">
                                    Contact our support team at{' '}
                                    <Link href="mailto:info@hubitable.co.uk" sx={{ color: 'primary.500' }}>
                                        info@hubitable.co.uk
                                    </Link> with:
                                </Typography>
                                <List size="sm" sx={{ mt: 2 }}>
                                    <ListItem>
                                        <ListItemContent>
                                            <Typography level="body2">
                                                Your account details
                                            </Typography>
                                        </ListItemContent>
                                    </ListItem>
                                    <ListItem>
                                        <ListItemContent>
                                            <Typography level="body2">
                                                Order number or transaction ID
                                            </Typography>
                                        </ListItemContent>
                                    </ListItem>
                                    <ListItem>
                                        <ListItemContent>
                                            <Typography level="body2">
                                                Reason for the refund request
                                            </Typography>
                                        </ListItemContent>
                                    </ListItem>
                                </List>
                            </ListItemContent>
                        </ListItem>
                        <ListItem>
                            <ListItemContent>
                                <Typography level="body1">
                                    We aim to respond to refund requests within 2 business days.
                                </Typography>
                            </ListItemContent>
                        </ListItem>
                    </List>

                    <Divider sx={{ my: 4 }} />

                    <Typography level="h2" sx={{ mb: 3, color: 'text.primary' }}>
                        4. Exclusions
                    </Typography>
                    <List size="lg" sx={{ mb: 4 }}>
                        <ListItem>
                            <ListItemContent>
                                <Typography level="body1">
                                    Refunds may not be available for:
                                </Typography>
                                <List size="sm" sx={{ mt: 2 }}>
                                    <ListItem>
                                        <ListItemContent>
                                            <Typography level="body2">
                                                Services already used or consumed
                                            </Typography>
                                        </ListItemContent>
                                    </ListItem>
                                    <ListItem>
                                        <ListItemContent>
                                            <Typography level="body2">
                                                Custom or personalized services
                                            </Typography>
                                        </ListItemContent>
                                    </ListItem>
                                    <ListItem>
                                        <ListItemContent>
                                            <Typography level="body2">
                                                Services where you've waived your right to cancel
                                            </Typography>
                                        </ListItemContent>
                                    </ListItem>
                                </List>
                            </ListItemContent>
                        </ListItem>
                    </List>

                    <Divider sx={{ my: 4 }} />

                    <Typography level="h2" sx={{ mb: 3, color: 'text.primary' }}>
                        5. Contact Us
                    </Typography>
                    <Typography level="body1" sx={{ mb: 4, color: 'text.secondary' }}>
                        For questions about our refund policy, please contact us at{' '}
                        <Link href="mailto:info@hubitable.co.uk" sx={{ color: 'primary.500' }}>
                            info@hubitable.co.uk
                        </Link>.
                    </Typography>
                </Box>
            </Container>
        </Box>
    );
};

export default Refund; 